import {
  BehaviorSubject,
  LoadingProgressStatus,
  LoadingProgressStatusPhase,
  Public
} from '@app/types';
import {
  HttpContentType,
  HttpFetchProps,
  HttpAdapter,
  JsonResponse
} from './types';

type HttpBodyInfo = {
  body: RequestInit['body'];
  contentType?: HttpContentType;
};

/** Public interface of @see HttpService. */
export type IHttpService = Public<HttpService>;

/** Service used to make HTTP requests. */
export class HttpService {
  private _adapters: HttpAdapter[];

  constructor(
    requestAdapters?: HttpAdapter[]
  ) {
    this._adapters = requestAdapters ?? [];
  }

  /**
   * Create a multipart form to send as the body of a request.
   *
   * @note Browser automatically sends `content-type: "multipart/form-data"` header.
   */
  createMultipartForm(data: Record<string, unknown>): FormData {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      const validValue = value instanceof Blob || typeof value === 'string';
      const formValue = (validValue) ? value : (
        new Blob([JSON.stringify(value)], { type: 'application/json' })
      );
      formData.append(key, formValue);
    });

    return formData;
  }

  /**
   * Create a URL form to send as the body of a request.
   *
   * @note Browser automatically sends `content-type: "application/x-www-form-urlencoded"` header.
   */
  createUrlForm(data: Record<string, string>): URLSearchParams {
    return new URLSearchParams(data);
  }

  /** Fetch a request. */
  async fetch(props: HttpFetchProps): Promise<Response> {
    const originalProps = props;
    for (const adapter of this._adapters) {
      props = (typeof adapter.adaptHttpRequest !== 'function') ? props : (
        await adapter.adaptHttpRequest(props)
      );
    }

    const {
      body,
      contentType
    } = this._getBodyInfo(props);
    props.headers ??= {};
    if (contentType) {
      props.headers['Content-Type'] ??= contentType;
    }

    let { progressMonitor, query, url, ...requestInit } = props;
    const queryString = Object.entries(query ?? {}).map(([key, value]) => {
      return `${key}=${encodeURIComponent(value)}`;
    }).join('&');
    if (query) {
      const separator = (url.includes('?')) ? '&' : '?';
      url = `${url}${separator}${queryString}`;
    }
    let response: Response;
    if (progressMonitor) {
      response = await this._fetchWithUploadProgress(
        url,
        { ...requestInit, body },
        progressMonitor
      );
    } else {
      response = await fetch(url, {
        ...requestInit,
        body
      });
    }
    for (const adapter of this._adapters) {
      response = (typeof adapter.adaptHttpResponse !== 'function') ? response : (
        await adapter.adaptHttpResponse({
          request: originalProps,
          response,
          service: this
        })
      );
    }
    return response;
  }

  /**
   * XHR-based fetch that emits upload progress events.
   *
   * @note This path tries to mimic `fetch`, but is imperfect, so it should only be used when progress is going to be displayed.
   */
  private _fetchWithUploadProgress(
    url: string,
    init: RequestInit,
    progressMonitor: BehaviorSubject<LoadingProgressStatus>
  ): Promise<Response> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open((init.method ?? 'GET').toUpperCase(), url, true);
      xhr.responseType = 'blob';
      const headers = (init.headers ?? {}) as Record<string, string>;
      for (const [key, value] of Object.entries(headers)) {
        xhr.setRequestHeader(key, value);
      }

      // Browsers set this automatically for FormData. Do not send anything
      // for it so the boundary is computed correctly.
      const body = init.body as XMLHttpRequestBodyInit | null | undefined;
      const fallbackTotal = getBodyByteLength(body);

      xhr.upload.addEventListener('progress', ({
        lengthComputable,
        loaded,
        total
      }) => {
        const isFinalizing = lengthComputable && total > 0 && loaded >= total;
        progressMonitor.next((isFinalizing) ? (
          { phase: LoadingProgressStatusPhase.finalizing }
        ) : (
          {
            loaded,
            phase: LoadingProgressStatusPhase.uploading,
            total: (lengthComputable) ? total : fallbackTotal
          }
        ));
      });

      xhr.addEventListener('load', () => {
        const rawHeaders = xhr.getAllResponseHeaders();
        const responseHeaders = new Headers();
        rawHeaders.trim().split(/[\r\n]+/).forEach((line) => {
          const separatorIndex = line.indexOf(':');
          if (separatorIndex < 0) {
            return;
          }
          const name = line.slice(0, separatorIndex).trim();
          const value = line.slice(separatorIndex + 1).trim();
          if (name) {
            responseHeaders.append(name, value);
          }
        });
        resolve(new Response(xhr.response, {
          headers: responseHeaders,
          status: xhr.status,
          statusText: xhr.statusText
        }));
      });
      xhr.addEventListener('error', () => {
        reject(new TypeError('Network request failed'));
      });
      const abort = () => {
        reject(new DOMException('The operation was aborted.', 'AbortError'));
      };
      xhr.addEventListener('abort', abort);

      const signal = init.signal;
      if (signal) {
        if (signal.aborted) {
          // xhr.abort does not work until xhr.send is called.
          abort();
          return;
        }
        signal.addEventListener('abort', () => xhr.abort());
      }

      xhr.send(body ?? null);
    });
  }

  /** Fetch a request and return parsed JSON. */
  async fetchJson<T>(props: HttpFetchProps): Promise<JsonResponse<T>> {
    try {
      const response = await this.fetch(props);
      if (!response.ok) {
        return { error: response };
      }

      return { value: await response.json() };
    } catch (error) {
      if (error instanceof Error) {
        return { error };
      } else {
        // This should not happen.
        throw error;
      }
    }
  }

  /** Fetch a request and return parsed JSON or throw an error. */
  async fetchJsonOrFail<T>(props: HttpFetchProps): Promise<T> {
    const { error, value } = await this.fetchJson<T>(props);
    if (error) {
      throw error;
    }
    return value;
  }

  /** Get information about the body to add to the request. */
  private _getBodyInfo({
    body
  }: HttpFetchProps): HttpBodyInfo {
    if (
      body == null
      || typeof body === 'string'
      || body instanceof FormData
      || body instanceof URLSearchParams
    ) {
      // Let content type get set automatically.
      return { body };
    }

    return {
      body: JSON.stringify(body),
      contentType: HttpContentType.applicationJson
    };
  }
}

function getBodyByteLength(
  body: XMLHttpRequestBodyInit | null | undefined
): number {
  if (body == null) {
    return 0;
  }
  if (typeof body === 'string') {
    return new Blob([body]).size;
  }
  if (body instanceof Blob) {
    return body.size;
  }
  if (body instanceof ArrayBuffer) {
    return body.byteLength;
  }
  if (ArrayBuffer.isView(body)) {
    return body.byteLength;
  }
  if (body instanceof URLSearchParams) {
    return new Blob([body.toString()]).size;
  }
  // FormData byte length depends on browser-computed boundaries; leave as 0.
  return 0;
}
